import { ListRolesRequest, RoleInfo } from '@models/role.model'
import { MyOrganizationInfo, OrganizationInfo } from '../../models/organization.model'
import {
	LandingPageProjectInfo,
	ListProjectsRequest,
	ProjectInfo,
} from '../../models/projects.model'
import { AuditTrail, ListUserRequest, UserInfo, UserList } from '../../models/user.model'
import API from '../index'
import { DateFilter } from '@models/common.model'

const CREATE_USER_FAILED_MESSAGE = 'Failed to create user'
const USER_ALREADY_EXISTS_MESSAGE = 'User already exist!'
const EXECUTE_ACTIONS_EMAIL_ERROR = 'Failed to send execute actions email'
const DUPLICATE_EMAIL_ERROR_HINTS = ['profile_email_key', 'SQLSTATE=23505']

const getErrorMessage = (error: any) => {
	if (typeof error === 'string') {
		return error
	}

	if (typeof error?.detail === 'string') {
		return error.detail
	}

	if (typeof error?.message === 'string') {
		return error.message
	}

	return ''
}

const isExecuteActionsEmailError = (error: any) => {
	return getErrorMessage(error).includes(EXECUTE_ACTIONS_EMAIL_ERROR)
}

const isDuplicateEmailError = (error: any) => {
	const message = getErrorMessage(error)
	return DUPLICATE_EMAIL_ERROR_HINTS.some((hint) => message.includes(hint))
}

const normalizeEmail = (email?: string) => {
	return email?.trim().toLowerCase() || ''
}

const createUserFailedError = () => {
	return { detail: CREATE_USER_FAILED_MESSAGE }
}

const createUserAlreadyExistsError = () => {
	return { detail: USER_ALREADY_EXISTS_MESSAGE }
}

const GetUserInfo = async () => {
	return await API.get<UserInfo>(`/api/v2/users/my-profile`).then((rep) => {
		return rep.data
	})
}

const GetUserOrganization = async () => {
	return await API.get<MyOrganizationInfo>(`/api/v2/users/my-profile/organization`).then((rep) => {
		return rep.data
	})
}

const GetUserLandingProjects = async () => {
	return await API.get<{ projects: LandingPageProjectInfo[] }>(
		`/api/v2/users/my-profile/landing-page-projects`,
		{ cache: false } as any,
	).then((rep) => {
		console.log('GetUserLandingProjects', rep.data)
		return rep.data
	})
}

const GetUserList = async (req: ListUserRequest) => {
	return await API.post<{ data: UserList[]; total_count: number }>(`/api/v2/users/search`, req, {
		timeout: 120000,
	}).then((rep) => {
		console.log('rep', rep)
		return rep.data
	})
}

const GetUsersOptions = async (req: ListUserRequest) => {
	return await API.post(`/api/v2/users/options`, req).then((rep) => {
		return rep.data
	})
}

const GetRolesOptions = async (req: ListUserRequest) => {
	return await API.post(`/api/v2/users/my-profile/roles/options`, req).then((rep) => {
		return rep.data
	})
}

const GetProjectsOptions = async (req: ListUserRequest) => {
	return await API.post(`/api/v2/users/my-profile/projects/options`, req).then((rep) => {
		return rep.data
	})
}
const UpdateUserProfile = async (req: UserInfo, id: string) => {
	return await API.put(`/api/v2/users/${id}`, req).then((rep) => {
		return rep.data
	})
}

const GetMyProfileProjectList = async (req: ListProjectsRequest) => {
	return await API.post<{
		data: {
			code: string
			display_name: string
			project_id: string
			org_ids: string[]
		}[]
		total_count: number
	}>(`/api/v2/users/my-profile/projects/search`, req).then((rep) => {
		return rep.data
	})
}

const GetUserProfile = async (profile_id: string) => {
	return await API.get<UserInfo>(`/api/v2/users/${profile_id}`).then((rep) => {
		return rep.data
	})
}

const CreateUser = async (req: UserInfo) => {
	return await API.post(`/api/v2/users`, req).then((rep) => {
		return rep.data
	})
}

const CreateUserWithRecovery = async (req: UserInfo) => {
	try {
		return await CreateUser(req)
	} catch (error) {
		if (isDuplicateEmailError(error)) {
			throw createUserAlreadyExistsError()
		}

		if (!isExecuteActionsEmailError(error)) {
			throw createUserFailedError()
		}
	}

	try {
		return await CreateUser(req)
	} catch (error) {
		if (isDuplicateEmailError(error)) {
			return
		}
	}

	try {
		const email = req.email?.trim()
		const normalizedEmail = normalizeEmail(email)
		if (!email || !normalizedEmail) {
			throw createUserFailedError()
		}

		const response = await GetUserList({
			email: {
				operator: '=',
				value: email,
			},
			pagination: {
				page: 1,
				page_size: 1,
			},
		})

		const isCreated = response.data?.some((user) => normalizeEmail(user.email) === normalizedEmail)
		if (isCreated) {
			return
		}
	} catch (error) {
		throw createUserFailedError()
	}

	throw createUserFailedError()
}

const GetMyProfileRoles = async (req: ListRolesRequest) => {
	return await API.post<{ data: RoleInfo[]; total_count: number }>(
		`api/v2/users/my-profile/roles/search`,
		req,
	).then((rep) => {
		return rep.data
	})
}

const GetProject = async (projectId: string) => {
	return await API.get<ProjectInfo>(`api/v2/projects/${projectId}`).then((rep) => {
		return rep.data
	})
}

const CreateUserGroup = async (req: any) => {
	return await API.post(`/api/v2/roles`, req).then((rep) => {
		return rep.data
	})
}

const GetUserGroup = async (roleId: string) => {
	return await API.get<RoleInfo>(`api/v2/roles/${roleId}`).then((rep) => {
		return rep.data
	})
}

const UpdateUserGroup = async (req: any, roleId: string) => {
	return await API.put(`api/v2/roles/${roleId}`, req).then((rep) => {
		return rep.data
	})
}

const GenProjectProductUrl = async (projectId: string, appId: string) => {
	return await API.get(`/api/v2/projects/${projectId}/${appId}/gen-url`).then((rep) => {
		return rep
	})
}

const GetAuditTrails = async (req: { date?: DateFilter; userId?: string }) => {
	return await API.post<{
		data: AuditTrail[]
	}>(`/api/v2/users/audit-trails/export`, req).then((rep) => {
		return rep
	})
}

const CreateAuditTrail = async (req: {
	date: string
	message: string
	eventType: string
	status: string
}) => {
	return await API.post<{
		data: AuditTrail[]
	}>(`/api/v2/users/audit-trails`, req).then((rep) => {
		return rep
	})
}

const CheckProfile = async () => {
	return await API.get(`/api/v2/users/check-profile`).then((rep) => {
		return rep
	})
}

const UserService = {
	GetUserInfo,
	GetUserOrganization,
	GetUserLandingProjects,
	UpdateUserProfile,
	GetUserList,
	GetMyProfileProjectList,
	GetUserProfile,
	CreateUser,
	CreateUserWithRecovery,
	GetMyProfileRoles,
	GetProject,
	CreateUserGroup,
	GetUserGroup,
	UpdateUserGroup,
	GenProjectProductUrl,
	GetUsersOptions,
	GetRolesOptions,
	GetProjectsOptions,
	CheckProfile,
	GetAuditTrails,
	CreateAuditTrail,
}

export default UserService
